import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FlaskConical, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1602052577122-f73b9710adba?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const user = await login(email.trim().toLowerCase(), password);
      toast.success(`Selamat datang, ${user.name}`);
      navigate("/dashboard");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "Gagal masuk";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const quick = (em) => {
    setEmail(em);
    setPassword(em === "admin@hplc.lab" ? "admin123" : em === "dosen@hplc.lab" ? "dosen123" : "student123");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand / image */}
      <div className="relative hidden lg:block instrument-surface">
        <img src={BG} alt="Peralatan laboratorium" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 grid-overlay" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-md bg-primary flex items-center justify-center">
              <FlaskConical className="h-6 w-6 text-primary-foreground" />
            </div>
            <span className="font-heading font-extrabold text-xl text-white">HPLC Virtual Lab</span>
          </div>
          <div className="max-w-md">
            <h1 className="font-heading font-extrabold text-4xl text-white leading-tight mb-4">
              Belajar kromatografi cair kinerja tinggi secara interaktif.
            </h1>
            <p className="text-zinc-300 text-base">
              Simulasi berbasis fisika, analisis kromatogram, skenario praktikum, dan proyek —
              dalam satu laboratorium virtual yang dapat dipasang di perangkat Anda.
            </p>
          </div>
          <div className="font-data text-xs text-zinc-500">Reservoir → Pompa → Injektor → Kolom → Detektor → Komputer</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-background">
        <div className="w-full max-w-sm fade-in-up">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="h-10 w-10 rounded-md bg-primary flex items-center justify-center">
              <FlaskConical className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="font-heading font-extrabold text-lg">HPLC Virtual Lab</span>
          </div>
          <h2 className="font-heading font-extrabold text-2xl mb-1">Masuk</h2>
          <p className="text-sm text-muted-foreground mb-6">Gunakan email/NIM dan kata sandi Anda.</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="nama@hplc.lab"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Kata Sandi</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <p className="text-sm text-destructive font-medium" data-testid="login-error">
                {error}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Masuk
            </Button>
          </form>

          <div className="mt-8 border-t border-border pt-4">
            <p className="text-xs text-muted-foreground mb-2">Akun demo (klik untuk isi otomatis):</p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => quick("budi@hplc.lab")} data-testid="demo-student">Mahasiswa</Button>
              <Button variant="outline" size="sm" onClick={() => quick("dosen@hplc.lab")} data-testid="demo-lecturer">Dosen</Button>
              <Button variant="outline" size="sm" onClick={() => quick("admin@hplc.lab")} data-testid="demo-admin">Admin</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
